import type { CarriedAttachmentPayload } from '@/lib/medcom/threadAttachments';
import { conformingAttachmentCreation } from '@/pages/communicate/attachmentCreation';

/**
 * New CareCommunication payloads.
 *
 * IG: Communication.payload.extension:date (medcom-core-datetime-extension) is
 * the send press, on both string and attachment segments. It must be the same
 * instant as Bundle.timestamp and Provenance.occurredDateTime/recorded, so the
 * browser does not set it: communicate-send stamps one server send time onto
 * Bundle.timestamp and every new segment (see
 * supabase/functions/_shared/communicate-send-time.ts).
 * payloads[].attachment.creation is File.lastModified (Europe/Copenhagen), or
 * storage created_at when lastModified is missing/0. Forwarded originals omit
 * this field here; Communicate restores it. The identifier shown in the form is
 * the one sent; it is never regenerated here.
 */

export type NewTextPayload = {
  kind: 'string';
  content: string;
};

export type NewAttachmentDraft = {
  identifier: string;
  contentType: string;
  title?: string;
  data?: string;
  url?: string;
  /** Document time → Attachment.creation. Not the payload datetime-extension. */
  creation?: string | null;
};

export type NewAttachmentPayload = {
  kind: 'attachment';
  identifier: string;
  attachment: {
    contentType: string;
    title?: string;
    data?: string;
    url?: string;
    creation?: string;
  };
};

export type OutgoingPayload = NewTextPayload | NewAttachmentPayload | CarriedAttachmentPayload;

export function newTextPayload(content: string): NewTextPayload {
  return { kind: 'string', content };
}

/**
 * A file or URL added on this send. No authoredAt: the server sets the send time.
 * The identifier is the one the form showed. creation stays the document time
 * when it conforms; URLs omit creation.
 */
export function newAttachmentPayload(draft: NewAttachmentDraft): NewAttachmentPayload {
  const creation = draft.url
    ? undefined
    : conformingAttachmentCreation(draft.creation);
  const payload: NewAttachmentPayload = {
    kind: 'attachment',
    identifier: draft.identifier,
    attachment: {
      contentType: draft.contentType,
    },
  };
  if (draft.title !== undefined) payload.attachment.title = draft.title;
  if (draft.data) payload.attachment.data = draft.data;
  if (draft.url) payload.attachment.url = draft.url;
  if (creation) payload.attachment.creation = creation;
  return payload;
}

/**
 * Order: new text, copied attachments (original authoredAt), then new files.
 * Copied items keep their original payload date and identifier.
 */
export function composeOutgoingPayloads(input: {
  text: string;
  includeEmptyText?: boolean;
  carried?: readonly CarriedAttachmentPayload[];
  newAttachments?: readonly NewAttachmentDraft[];
}): OutgoingPayload[] {
  const payloads: OutgoingPayload[] = [];
  if (input.includeEmptyText || input.text.trim()) {
    payloads.push(newTextPayload(input.text));
  }
  for (const carried of input.carried ?? []) {
    payloads.push(carried);
  }
  for (const draft of input.newAttachments ?? []) {
    payloads.push(newAttachmentPayload(draft));
  }
  return payloads;
}
